import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  useTheme,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import AddProduct from "../../Components/Common/productPopUp"; // ✅ import your AddProduct drawer
import Axios from "../../Common/api/axiosConfig";
import { StoreData } from "../../Components/Common/breadcrumbs";

interface QuickActionsProps {
  storeData: StoreData | null;
  fetchStoreData: () => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setOpenSnackbar: React.Dispatch<React.SetStateAction<boolean>>;
}

const quickActions = [
  { title: "Add Product", icon: <AddBoxIcon fontSize="large" /> },
  { title: "View Store", icon: <StorefrontIcon fontSize="large" /> },
  { title: "Test Order", icon: <ShoppingCartIcon fontSize="large" /> },
  { title: "Share Store", icon: <ShareIcon fontSize="large" /> },
];

const QuickActions: React.FC<QuickActionsProps> = ({
  storeData,
  fetchStoreData,
  setError,
  setOpenSnackbar,
}) => {
  const theme = useTheme();
  const [showProduct, setShowProduct] = useState(false);

  const handleActionClick = async (title: string) => {
    if (title === "Add Product") {
      setShowProduct(true);
    } else if (title === "View Store") {
      if (!storeData) await fetchStoreData();
      if (storeData?.url) {
        window.open(`http://localhost:3000/${storeData.url}`, "_blank");
      } else {
        setError("Store URL not found. Please create or reload your store.");
        setOpenSnackbar(true);
      }
    } else if (title === "Share Store") {
      if (!storeData) await fetchStoreData();
      if (storeData?.url) {
        const url = `http://localhost:3000/${storeData.url}`;
        try {
          await navigator.clipboard.writeText(url);
          alert("Store link copied to clipboard!");
        } catch {
          setError("Failed to copy store link.");
        }
        setOpenSnackbar(true);
      } else {
        setError("Store URL not found. Please create or reload your store.");
        setOpenSnackbar(true);
      }
    } else {
      console.log(`${title} clicked`);
    }
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          p: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Title and Subtitle */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Quick Action
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your Rudrix store efficiently
        </Typography>

        {/* Actions Grid */}
        <Grid container spacing={2}>
          {quickActions.map((action, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <CardActionArea onClick={() => handleActionClick(action.title)}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    height: 120,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "none",
                    textAlign: "center",
                    backgroundColor: theme.palette.background.default,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>{action.icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {action.title}
                  </Typography>
                </Card>
              </CardActionArea>
            </Grid>
          ))}
        </Grid>
      </Card>

      {/* ✅ Add Product Drawer */}
      <AddProduct
        open={showProduct}
        onClose={() => setShowProduct(false)}
        mode="create"
        onSubmit={(data) => {
          console.log("New Product Added:", data);
          setShowProduct(false);
        }}
      />
    </>
  );
};

export default QuickActions;
